/**
 * SOLID Review
 * S — Strong. One concern: what the `ClientAuthServer` socket answers while nothing is connected to it.
 * O — Strong. A contract op added later fails the contract-coverage test, not a cast.
 * L — Strong. Implements `IPortClientAuthServer` exactly: every op, the same answer.
 * I — Strong. The contract's ops and nothing else.
 * D — Strong. The kernel's failure builder and the port's interface.
 */
import { portFailure } from '@ml3d/kernel';
import type { IPortClientAuthServer } from './contracts';

/**
 * THE `ClientAuthServer` SOCKET'S NULL OBJECT (frontend-application-oo P6, 2026-10-28; `unavailable()` built it reflectively until
 * then). The frontend's failure policy (strict-ports FE1): a socket the assembly never connected degrades the move that needed
 * it — every op answers `UNAVAILABLE` — and the rest of the app still renders. The kernel guards it on the same terms as a real
 * provider, which is why it answers in the contract's own Result shape. The production-wiring test, not this, keeps an
 * unconnected socket from shipping: it asserts `assertAssembled` with an empty allowlist.
 */
export class UnavailableClientAuthServer implements IPortClientAuthServer {
  private readonly failure = portFailure('UNAVAILABLE', 'ClientAuthServer is not connected.');

  async account() {
    return this.failure;
  }

  async me() {
    return this.failure;
  }

  async signOut() {
    return this.failure;
  }

  async requestLoginCode() {
    return this.failure;
  }

  async register() {
    return this.failure;
  }

  async verifyCode() {
    return this.failure;
  }

  async validateReferral() {
    return this.failure;
  }

  async updateDisplayName() {
    return this.failure;
  }

  async requestEmailChange() {
    return this.failure;
  }

  async verifyEmailChange() {
    return this.failure;
  }
}
